import {
    constants,
    genreMatchesFilter,
    getTextDialFilterData,
    locale,
    removeNulls,
} from '@trqk/utils';

import { chartTypes, defaultFilterOrder } from '../../constants';

const { broadcastFilterPredicate, digitalFilterPredicate } = constants;

/**
 * Filters an array of elements by whether its `music_user_genre_name` matches a filter value.
 * @param list a list of either ProPerformanceContribution, ProShowNameContribution, or
 * ProSourceNameContribution elements
 * @param filter either 'broadcast' or 'digital'
 */
export function filterPerformanceData(
    list: PerformanceDataElement[],
    filter: BroadcastOrDigital,
): PerformanceDataElement[] {
    return list.filter(
        ({ music_user_genre_name }: PerformanceDataElement): boolean =>
            genreMatchesFilter(music_user_genre_name, filter),
    );
}

/**
 * @typedef {GetChartConfigsInput}
 * @property {ProTrack} proTrack track data
 * @property {number} maxSize max size to apply in filter function
 * @property {number} minValue min earnings to apply in filter function
 * @property {boolean} truncateChartData whether to filter and slice chart data
 */
interface GetChartConfigsInput {
    trackData: ProTrack;
    maxSize: number;
    minValue: number;
    truncateChartData: boolean;
}

/**
 * Given a PROTRack, returns a Map with the necessary details for rendering the charts in
 * PerformanceCharts. A Map is used because we need to establish the order of the dropdown options
 * in the chart selector as well as refer to the chart configs by key.
 * @param trackData {ProTrack}
 * @returns a Map where each key is a PerformanceChartType and each value is a
 * PerformanceChartConfig
 */
export const getChartConfigs = ({
    trackData,
    maxSize,
    minValue,
    truncateChartData,
}: GetChartConfigsInput): Map<PerformanceChartType, PerformanceChartConfig> => {
    const showEarningsData = getTextDialFilterData<ProShowNameContribution>({
        data: removeNulls(trackData.show_name_contributions),
        filterConfigs: {
            broadcast: {
                filterPredicate: broadcastFilterPredicate,
                maxSize,
                sort: {
                    key: 'earnings_contribution',
                    order: 'desc',
                },
                smallValueFilter: {
                    key: 'earnings_contribution',
                    min: minValue,
                },
            },
            digital: {
                filterPredicate: digitalFilterPredicate,
                maxSize,
                sort: {
                    key: 'earnings_contribution',
                    order: 'desc',
                },
                smallValueFilter: {
                    key: 'earnings_contribution',
                    min: minValue,
                },
            },
        },
        defaultFilterOrder,
        truncateChartData,
    });

    const sourceEarningsData = getTextDialFilterData<ProSourceNameContribution>(
        {
            data: removeNulls(trackData.source_name_contributions),
            filterConfigs: {
                broadcast: {
                    filterPredicate: broadcastFilterPredicate,
                    // we are not filtering by maxSize for sourceEarningsData because it is rendered
                    // in a pie chart
                    sort: {
                        key: 'earnings_contribution',
                        order: 'desc',
                    },
                    smallValueFilter: {
                        key: 'earnings_contribution',
                        min: minValue,
                    },
                },
                digital: {
                    filterPredicate: digitalFilterPredicate,
                    // we are not filtering by maxSize for sourceEarningsData because it is rendered
                    // in a pie chart
                    sort: {
                        key: 'earnings_contribution',
                        order: 'desc',
                    },
                    smallValueFilter: {
                        key: 'earnings_contribution',
                        min: minValue,
                    },
                },
            },
            defaultFilterOrder,
            truncateChartData,
        },
    );

    const sourcePlaysData = getTextDialFilterData<ProSourceNameContribution>({
        data: removeNulls(trackData.source_name_contributions),
        filterConfigs: {
            broadcast: {
                filterPredicate: broadcastFilterPredicate,
                maxSize,
                sort: {
                    key: 'plays_contribution',
                    order: 'desc',
                },
                smallValueFilter: {
                    key: 'plays_contribution',
                    min: minValue,
                },
            },
            digital: {
                filterPredicate: digitalFilterPredicate,
                maxSize,
                sort: {
                    key: 'plays_contribution',
                    order: 'desc',
                },
                smallValueFilter: {
                    key: 'plays_contribution',
                    min: minValue,
                },
            },
        },
        defaultFilterOrder,
        truncateChartData,
    });

    return new Map([
        [
            chartTypes.showEarnings,
            {
                filterData: showEarningsData,
                dropdownOption: {
                    value: chartTypes.showEarnings,
                    label: locale.enUS.TRACK_DETAIL.PERFORMANCE_CHART_LABELS
                        .SHOW_EARNINGS,
                },
            },
        ],
        [
            chartTypes.sourceEarnings,
            {
                filterData: sourceEarningsData,
                dropdownOption: {
                    value: chartTypes.sourceEarnings,
                    label: locale.enUS.TRACK_DETAIL.PERFORMANCE_CHART_LABELS
                        .SOURCE_EARNINGS,
                },
            },
        ],
        [
            chartTypes.sourcePlays,
            {
                filterData: sourcePlaysData,
                dropdownOption: {
                    value: chartTypes.sourcePlays,
                    label: locale.enUS.TRACK_DETAIL.PERFORMANCE_CHART_LABELS
                        .SOURCE_PLAYS,
                },
            },
        ],
    ]);
};
